import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router";
import Select from 'react-select';
import bookOverview from '../../../types/BookOverviewType';
import isbnType from '../../../types/IsbnType';
import userType from '../../../types/userType';
import InputField from '../../01_atoms/InputField/InputField';
import Loader from '../../01_atoms/Loader/Loader';
import placeholder from '../../../resources/images/placeholder.png';
import useAPI from '../../../hooks/useAPI';

interface addBookFormProps {
  isbnData: isbnType | undefined;
  bookData?: bookOverview;
  editForm?: boolean;
  isbnNumber?: string | undefined;
}

interface optionsType {
  value: string;
  label: string;
}

const ALLOWED_COVER_HOSTS = new Set(['media.s-bol.com', 'spurt.kortrijk.be', 'covers.openlibrary.org', 'books.google.com']);

function isAllowedCoverUrl(url: string): boolean {
  if (!url) return true;
  try {
    const parsed = new URL(url);
    return ALLOWED_COVER_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

function AddBookForm(props: addBookFormProps) {
  const { isbnData, editForm, bookData, isbnNumber } = props;
  const navigate = useNavigate();
  const { Get, Post, Put } = useAPI();

  const [lockForm, setLockForm] = useState(false);

  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [owner, setOwner] = useState('');
  const [description, setDescription] = useState('');
  const [isAvailable, setIsAvailable] = useState('Beschikbaar');
  const [imgUrl, setImgUrl] = useState('');
  const [imgLoading, setImgLoading] = useState(false);
  const [options, setOptions] = useState<optionsType[]>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [loanedOutBy, setLoanedOutBy] = useState<any>('a87bbeb5-3702-4a1d-b8d7-fa4d7fdab72f');

  const getUsers = async () => {
    const optionsvalue: optionsType[] = [];
    const data: userType[] = await Get(`/admin/users`);
    data.forEach((user: userType) => {
      optionsvalue.push({ value: String(user.id), label: user.name });
      if (isAvailable === 'Uitgeleend' && bookData?.currentLender === user.name)
        setLoanedOutBy({ value: String(user.id), label: user.name });
    });
    setOptions(optionsvalue);
  };

  useEffect(() => {
    if (bookData) {
      if (bookData.title) setTitle(bookData.title);
      if (bookData.author) setAuthor(bookData.author);
      if (bookData.owner) setOwner(bookData.owner);
      if (bookData.description) setDescription(bookData.description);
      if (bookData.available !== null) {
        if (bookData.available) setIsAvailable('Beschikbaar');
        else setIsAvailable('Uitgeleend');
      }
      if (bookData.coverBookUrl !== null) setImgUrl(bookData.coverBookUrl);
      else setImgUrl('');
    }
    if (isbnData) {
      if (isbnData.subtitle) setTitle(`${isbnData.title} - ${isbnData.subtitle}`);
      else setTitle(isbnData.title);

      if (isbnData.author) setAuthor(isbnData.author);
      if (isbnData.description) setDescription(isbnData.description);
      if (isbnData.coverBookUrl) setImgUrl(isbnData.coverBookUrl);
    }
    getUsers();
  }, [bookData, isbnData]);

  useEffect(() => {
    if (isAvailable === 'Uitgeleend') getUsers();
  }, [isAvailable]);

  useEffect(() => {
    setImgLoading(imgUrl !== '');
  }, [imgUrl]);

  const handleForm = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (!isAllowedCoverUrl(imgUrl)) {
      const e = new CustomEvent('ShowAlert', {
        detail: {
          label: 'Cover url is niet toegelaten, gebruik een geldige bron',
          type: 'Warning',
        },
      });
      window.dispatchEvent(e);
      return;
    }

    setLockForm(true);
    let available = true;
    if (isAvailable === 'Beschikbaar') available = true;
    else available = false;

    let boekData;

    if (available) {
      boekData = {
        title,
        subtitle: isbnData?.subtitle || title,
        description,
        available,
        author,
        owner,
        coverBookUrl: imgUrl,
        isbn: isbnNumber,
      };
    } else {
      boekData = {
        title,
        subtitle: isbnData?.subtitle || title,
        description,
        available,
        author,
        owner,
        coverBookUrl: imgUrl,
        isbn: isbnNumber,
        userId: loanedOutBy.value,
      };
    }
    try {
      if (editForm) {
        await Put(`/admin/books/${bookData?.id}`, boekData);
        navigate('/admin');
        const e = new CustomEvent('ShowAlert', {
          detail: {
            label: 'Boek succesvol aangepast',
            type: 'Success',
          },
        });
        window.dispatchEvent(e);
      } else {
        await Post(`/admin/books`, boekData);
        navigate('/admin');
        const e = new CustomEvent('ShowAlert', {
          detail: {
            label: 'Boek succesvol toegevoegd',
            type: 'Success',
          },
        });
        window.dispatchEvent(e);
      }
    } catch (error) {
      if (editForm) {
        const e = new CustomEvent('ShowAlert', {
          detail: {
            label: 'Boek bewerken is niet gelukt',
            type: 'Warning',
          },
        });
        window.dispatchEvent(e);
      } else {
        const e = new CustomEvent('ShowAlert', {
          detail: {
            label: 'Boek toevoegen is niet gelukt',
            type: 'Warning',
          },
        });
        window.dispatchEvent(e);
      }
    }
    setLockForm(false);
  };

  return (
    <form id="add-book-form" onSubmit={handleForm}>
      <fieldset className="o-form" disabled={lockForm}>
        {/* Titel */}
        <div className="o-form-item__3-cols">
          <InputField
            labelName="Titel"
            type="text"
            placeholder="Titel"
            value={title}
            onChange={setTitle}
            isRequired
          />
        </div>

        {/* Auteur */}
        <div className="o-form-item__2-cols">
          <InputField
            labelName="Auteur"
            type="text"
            placeholder="Auteur"
            value={author}
            onChange={setAuthor}
            isRequired
          />
        </div>

        {/* Eigenaar */}
        <div className="o-form-item__2-cols">
          <InputField
            labelName="Eigenaar"
            type="text"
            placeholder="Eigenaar"
            value={owner}
            onChange={setOwner}
            isRequired
          />
        </div>

        {/* Korte inhoud */}
        <div className="o-form-item__2-cols o-form__summary">
          <InputField
            labelName="Korte inhoud"
            type="textarea"
            placeholder="Korte inhoud"
            value={description}
            onChange={setDescription}
            isRequired
          />
        </div>

        <div className={`o-form__cover-wrapper ${imgUrl === '' ? 'o-form__cover-wrapper--placeholder' : ''}`}>
          {imgLoading && (
            <div className="o-form__cover-loading">
              <Loader />
            </div>
          )}
          <img
            className={`o-form__cover ${imgUrl === '' ? 'o-form__cover--placeholder' : ''} ${imgLoading ? 'o-form__cover--hidden' : ''
              }`}
            src={imgUrl === '' ? placeholder : imgUrl}
            alt={`Boekomslag van ${title}`}
            onLoad={() => setImgLoading(false)}
            onError={() => setImgLoading(false)}
          />
        </div>

        {/* Status */}
        <div className="o-form-item o-form-item__2-cols">
          <InputField
            labelName="Status"
            type="radio"
            value={isAvailable}
            onChange={setIsAvailable}
            radioValues={['Beschikbaar', 'Uitgeleend']}
            isRequired
          />
          {isAvailable === 'Uitgeleend' ? (
            <div className="o-form__radio-uitgeleend">
              <Select
                value={loanedOutBy}
                onChange={(option) => setLoanedOutBy({ value: option?.value, label: option.label })}
                options={options}
                className="o-form__select"
                classNamePrefix="o-form__select"
                placeholder="Naam"
              />
            </div>
          ) : null}
        </div>

        {/* Cover url */}
        <InputField
          labelName="Cover boek (url)"
          type="url"
          placeholder="img url"
          value={imgUrl}
          onChange={setImgUrl}
        />
      </fieldset>
    </form>
  );
}

AddBookForm.defaultProps = {
  bookData: null,
  editForm: false,
  isbnNumber: null,
};

export default AddBookForm;

